import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { dataBR } from "@vetbolso/core";
import { api, ler } from "../api.ts";
import { Cabecalho, Carregando, Cartao, dataHora, Selo, STATUS_FICHA, Vazio } from "../ui.tsx";

export function PaginaPainel() {
  const p = useQuery({ queryKey: ["pendencias"], queryFn: () => ler(api.pendencias.$get()) }).data;
  const rel = useQuery({ queryKey: ["previa"], queryFn: () => ler(api.releases.previa.$get()) }).data;

  return (
    <>
      <Cabecalho titulo="Painel" sub="O que precisa de atenção antes da próxima publicação" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Object.entries(STATUS_FICHA).map(([s, info]) => (
          <Link key={s} to="/fichas" search={{ status: s }} className="rounded-xl border border-line bg-surface p-4 hover:border-primary">
            <div className="font-mono text-2xl font-semibold text-ink">{p?.porStatus.find((x) => x.status === s)?.n ?? 0}</div>
            <div className="text-sm text-muted">{info.rotulo}</div>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao
          titulo="Release"
          acoes={
            <Link to="/publicar" className="text-sm font-semibold text-primary">
              Abrir publicação →
            </Link>
          }
        >
          {rel ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted">Em vigor</dt>
              <dd className="font-mono">{rel.atual ? `${rel.atual.versao} · ${dataHora(rel.atual.publicadoEm)}` : "nenhuma"}</dd>
              <dt className="text-muted">Mudanças aprovadas</dt>
              <dd>{rel.alteradas.length ? rel.alteradas.map((a) => a.nome).join(", ") : "nenhuma"}</dd>
              <dt className="text-muted">Validador</dt>
              <dd>{rel.erros.length ? <Selo tom="perigo">{rel.erros.length} problema(s)</Selo> : <Selo tom="ok">sem bloqueios</Selo>}</dd>
            </dl>
          ) : (
            <Carregando />
          )}
        </Cartao>

        <Cartao titulo={`Fichas com erro de auditoria (${p?.comErros.length ?? 0})`}>
          {p?.comErros.length ? (
            <ul className="flex flex-col gap-2 text-sm">
              {p.comErros.slice(0, 8).map((f) => (
                <li key={f.id}>
                  <Link to="/fichas/$id" params={{ id: f.id }} className="font-semibold text-ink hover:text-primary">
                    {f.titulo}
                  </Link>
                  <p className="text-xs text-danger">{(f.erros as string[])[0]}</p>
                </li>
              ))}
            </ul>
          ) : (
            <Vazio>Nenhuma ficha com erro.</Vazio>
          )}
        </Cartao>

        <Cartao titulo={`Refs pendentes de confirmação (${p?.pendentes.length ?? 0} fichas)`}>
          <ul className="flex max-h-72 flex-col gap-1 overflow-auto text-sm">
            {p?.pendentes.map((f) => (
              <li key={f.id} className="flex justify-between gap-2">
                <Link to="/fichas/$id" params={{ id: f.id }} className="truncate hover:text-primary">
                  {f.titulo}
                </Link>
                <Selo tom="alerta">{f.n}</Selo>
              </li>
            ))}
          </ul>
        </Cartao>

        <Cartao titulo={`Fontes conferidas há mais de 12 meses (${p?.antigas.length ?? 0})`}>
          {p?.antigas.length ? (
            <ul className="flex max-h-72 flex-col gap-1 overflow-auto text-sm">
              {p.antigas.map((f) => (
                <li key={f.id} className="flex justify-between gap-2">
                  <Link to="/fontes/$id" params={{ id: f.id }} className="truncate hover:text-primary">
                    {f.sigla}
                  </Link>
                  <span className="font-mono text-xs text-muted">{dataBR(f.acesso)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Vazio>Todas as fontes foram conferidas no último ano.</Vazio>
          )}
        </Cartao>

        <Cartao titulo={`Fatos em uso que não estão verificados (${p?.fatosNaoVerificados.length ?? 0})`}>
          <ul className="flex max-h-72 flex-col gap-2 overflow-auto text-sm">
            {p?.fatosNaoVerificados.map((f) => (
              <li key={f.id}>
                <Link to="/fatos/$id" params={{ id: f.id }} className="font-mono text-xs font-semibold text-primary">
                  {f.id}
                </Link>{" "}
                <Selo tom={f.status === "conflito" ? "perigo" : "alerta"}>{f.status}</Selo>
                <p className="text-xs text-muted">{f.afirmacao}</p>
              </li>
            ))}
          </ul>
        </Cartao>

        <Cartao titulo={`Lacunas conhecidas (${p?.lacunas.length ?? 0})`}>
          <ul className="flex max-h-72 list-disc flex-col gap-1.5 overflow-auto pl-4 text-sm">
            {p?.lacunas.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
          <Link to="/fichas/$id" params={{ id: "lacunas" }} className="text-sm font-semibold text-primary">
            Editar lista →
          </Link>
        </Cartao>
      </div>
    </>
  );
}
